import {Component, lazy, Suspense} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Accordion, AccordionDetails, AccordionSummary, Box, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {
  formatBitrate,
  formatSeconds,
  playerStateLabel,
  relayStateLabel
} from 'utils/ultraPlayerStats';

const UltraPlayerHistoryChart = lazy(() => import('./UltraPlayerHistoryChart'));

class ChartErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {failed: false};
  }

  static getDerivedStateFromError() {
    return {failed: true};
  }

  render() {
    if (this.state.failed) {
      return (
        <Box
          role="status"
          sx={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
        >
          El historial no está disponible. La reproducción continúa con normalidad.
        </Box>
      );
    }
    return this.props.children;
  }
}

ChartErrorBoundary.propTypes = {children: PropTypes.node.isRequired};

function metric(label, value) {
  return {label, value: value ?? '—'};
}

function trendLabel(value) {
  if (!Number.isFinite(value)) return '—';
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(1).replace('.', ',')} s / 5 s`;
}

function UltraPlayerStatsPanel({history, now, presentation, diagnostics}) {
  const summary = [
    metric('Reserva del relay', formatSeconds(presentation.reserveSeconds)),
    metric('Buffer local', formatSeconds(diagnostics.localBufferSeconds)),
    metric('Tendencia (5 s)', trendLabel(diagnostics.reserveTrend)),
    metric('Estado', presentation.label)
  ];
  const technicalGroups = [
    {
      title: 'Reproducción',
      items: [
        metric('Estado del player', playerStateLabel(diagnostics.playerState)),
        metric('Transporte', diagnostics.transport || '—')
      ]
    },
    {
      title: 'HLS y relay',
      items: [
        metric('Estado del relay', relayStateLabel(diagnostics.relayState)),
        metric('Borde publicado', formatSeconds(diagnostics.edgeDistanceSeconds)),
        metric('Objetivo HLS', formatSeconds(diagnostics.hlsTargetSeconds)),
        metric('Nivel HLS', diagnostics.levelLabel || '—'),
        metric('Bitrate declarado', formatBitrate(diagnostics.declaredBitrate)),
        metric('Ancho de banda estimado', formatBitrate(diagnostics.bandwidthEstimate))
      ]
    },
    {
      title: 'Vídeo',
      items: [
        metric(
          'Resolución',
          diagnostics.width && diagnostics.height
            ? `${diagnostics.width} × ${diagnostics.height}`
            : '—'
        ),
        metric(
          'Frames perdidos',
          Number.isFinite(diagnostics.droppedFrames) ? String(diagnostics.droppedFrames) : '—'
        )
      ]
    },
    {
      title: 'Conectividad',
      items: [metric('Red', diagnostics.online ? 'En línea' : 'Sin conexión')]
    },
    {
      title: 'Reintentos',
      items: [
        metric(
          'Reconexión',
          diagnostics.retryAttempt > 0 ? `Intento ${diagnostics.retryAttempt}` : 'Sin reintentos'
        )
      ]
    }
  ];

  return (
    <Box
      component="section"
      aria-label="Datos avanzados del streaming Ultra"
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        p: {xs: 1.5, sm: 2}
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5}}>
        <Box sx={{minWidth: 0}}>
          <Typography variant="h6" sx={{fontWeight: 800}}>
            Diagnóstico Ultra
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reserva del relay y buffer local, siempre medidos por separado.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))'
          },
          gap: 1,
          mb: 2
        }}
      >
        {summary.map((item) => (
          <Box
            key={item.label}
            sx={{
              minWidth: 0,
              p: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'background.paper'
            }}
          >
            <Typography variant="caption" color="text.secondary" component="div">
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{fontWeight: 750, wordBreak: 'break-word'}}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="subtitle2" sx={{mb: 0.75, fontWeight: 750}}>
        Evolución · últimos 60 segundos
      </Typography>
      <Box sx={{height: {xs: 230, sm: 280}, width: '100%'}}>
        <ChartErrorBoundary>
          <Suspense
            fallback={
              <Box
                role="status"
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}
              >
                Cargando historial…
              </Box>
            }
          >
            <UltraPlayerHistoryChart history={history} now={now} />
          </Suspense>
        </ChartErrorBoundary>
      </Box>

      <Accordion
        disableGutters
        variant="outlined"
        slotProps={{transition: {unmountOnExit: true}}}
        sx={{mt: 2, borderRadius: '8px !important', '&::before': {display: 'none'}}}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="ultra-technical-details-content"
          id="ultra-technical-details-header"
        >
          <Typography sx={{fontWeight: 750}}>Detalles técnicos</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{pt: 0}}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))'
              },
              gap: 1
            }}
          >
            {technicalGroups.map((group) => (
              <Box
                key={group.title}
                component="section"
                sx={{p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover', minWidth: 0}}
              >
                <Typography variant="subtitle2" sx={{mb: 0.75, fontWeight: 800}}>
                  {group.title}
                </Typography>
                <Box component="dl" sx={{m: 0, display: 'grid', gap: 0.75}}>
                  {group.items.map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                        gap: 1
                      }}
                    >
                      <Typography component="dt" variant="caption" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography
                        component="dd"
                        variant="caption"
                        sx={{m: 0, fontWeight: 750, textAlign: 'right', wordBreak: 'break-word'}}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

const nullableNumber = PropTypes.number;

UltraPlayerStatsPanel.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      at: PropTypes.number.isRequired,
      reserveSeconds: nullableNumber,
      localBufferSeconds: nullableNumber,
      targetReserveSeconds: nullableNumber
    })
  ).isRequired,
  now: PropTypes.number.isRequired,
  presentation: PropTypes.shape({
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    reserveSeconds: nullableNumber
  }).isRequired,
  diagnostics: PropTypes.shape({
    localBufferSeconds: nullableNumber,
    reserveTrend: nullableNumber,
    relayState: PropTypes.string,
    playerState: PropTypes.string,
    edgeDistanceSeconds: nullableNumber,
    hlsTargetSeconds: nullableNumber,
    transport: PropTypes.string,
    width: nullableNumber,
    height: nullableNumber,
    declaredBitrate: nullableNumber,
    bandwidthEstimate: nullableNumber,
    levelLabel: PropTypes.string,
    droppedFrames: nullableNumber,
    online: PropTypes.bool.isRequired,
    retryAttempt: PropTypes.number.isRequired
  }).isRequired
};

export default UltraPlayerStatsPanel;
